/* Search drawer — defines window.OdditSearchDrawer, which
   assets/oddit-section-header.js has been calling into all along.

   Mirrors assets/oddit-section-cart-drawer.js: an IIFE, one AbortController
   whose signal is passed to every listener, teardown on shopify:section:unload.
   Two deliberate departures from that file, both load-bearing:

   1. Focus goes to the <input> on open, not the panel. The cart drawer focuses
      its panel via tabindex="-1" to dodge a stray :focus-visible ring on a
      button. A search overlay exists to be typed into, and inputs don't paint
      an unwanted ring.
   2. The scroll lock is the ref-counted one from oddit-section-popup.js, not the
      cart drawer's plain overflow toggle. The mobile-menu handoff briefly has
      two overlays in flight and a non-counted lock double-unlocks. */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-search-drawer]');
  if (!drawer) return;

  var RESULTS_SECTION_ID = 'oddit-search-results';
  var DEBOUNCE_MS = 250;
  var CACHE_CAP = 30;
  var BODY_LOCK_CLASS = 'oddit-search-body-lock';

  // ---------- State ----------
  var abortController = new AbortController();
  var signal = abortController.signal;

  var isOpen = false;
  var lastFocusedElement = null;
  var lockCount = 0;
  var rafId = 0;

  var debounceTimer = null;
  var currentFetch = null;
  var requestSeq = 0;
  var cache = new Map();

  var options = [];
  var activeIndex = -1;
  var currentState = 'zero';
  var hasRenderedResults = false;
  var lastQuery = '';

  // ---------- DOM ----------
  var panel = drawer.querySelector('[data-search-panel]');
  var form = drawer.querySelector('[data-search-form]');
  var input = drawer.querySelector('[data-search-input]');
  var clearBtn = drawer.querySelector('[data-search-clear]');
  var zoneZero = drawer.querySelector('[data-search-zero]');
  var zoneResults = drawer.querySelector('[data-search-results]');
  var statusEl = drawer.querySelector('[data-search-status]');
  var inputSizer = null;

  // Everything below assumes these exist. Bail rather than throw on a partial render.
  if (!panel || !input || !zoneResults) return;

  // ---------- Config (from Liquid; never hardcode routes — multi-market stores
  // serve /en-ca/search/suggest) ----------
  var suggestUrl = drawer.getAttribute('data-suggest-url') || '/search/suggest';
  var predictiveEnabled = drawer.getAttribute('data-predictive-enabled') === 'true';
  var resourceTypes = drawer.getAttribute('data-resource-types') || 'product';
  var productLimit = parseInt(drawer.getAttribute('data-product-limit'), 10) || 6;
  var minChars = parseInt(drawer.getAttribute('data-min-chars'), 10) || 2;

  // Shopify caps resources[limit] at 10.
  if (productLimit > 10) productLimit = 10;
  if (productLimit < 1) productLimit = 1;

  // ---------- Utils ----------
  function getFocusableElements(container) {
    if (!container) return [];
    var selector = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    return Array.prototype.slice.call(container.querySelectorAll(selector))
      .filter(function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
  }

  function trapFocus(e) {
    if (!isOpen || e.key !== 'Tab') return;
    var focusable = getFocusableElements(panel);
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Ref-counted so a handoff from the mobile menu can't double-unlock.
  function lockBodyScroll() {
    lockCount += 1;
    if (lockCount === 1) {
      document.body.dataset.odditSearchPrevOverflow = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';
      document.body.classList.add(BODY_LOCK_CLASS);
    }
  }

  function unlockBodyScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.style.overflow = document.body.dataset.odditSearchPrevOverflow || '';
      delete document.body.dataset.odditSearchPrevOverflow;
      document.body.classList.remove(BODY_LOCK_CLASS);
    }
  }

  function announce(message) {
    if (statusEl) statusEl.textContent = message;
  }

  // ---------- Anchoring ----------
  // getBoundingClientRect().bottom is viewport-relative, which is exactly what a
  // position:fixed panel wants — do not add scrollY. Clamping at 0 means that if
  // the header has already scrolled away, the panel pins to the viewport top.
  function positionPanel() {
    if (window.matchMedia('(max-width: 1199px)').matches) {
      drawer.style.setProperty('--oddit-search-offset', '0px');
      return;
    }
    // Queried fresh each time: the theme editor re-renders the header.
    var header = document.querySelector('.oddit-header');
    var offset = 0;
    if (header) {
      var rect = header.getBoundingClientRect();
      if (rect.height > 0) offset = Math.max(0, rect.bottom);
    }
    drawer.style.setProperty('--oddit-search-offset', offset + 'px');
  }

  function onViewportChange() {
    if (!isOpen || rafId) return;
    rafId = requestAnimationFrame(function () {
      rafId = 0;
      positionPanel();
      syncInputWidth();
    });
  }

  // ---------- Open / close ----------
  function openDrawer() {
    if (isOpen) return;
    isOpen = true;
    lastFocusedElement = document.activeElement;

    positionPanel();
    drawer.style.display = '';
    void drawer.offsetWidth; // reflow, so the transition actually runs
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockBodyScroll();

    if (!input.value.trim()) showZeroState();
    syncClearButton();
    input.focus();
  }

  function closeDrawer() {
    if (!isOpen) return;
    isOpen = false;
    cancelInFlight();

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockBodyScroll();
    clearActive();
    announce('');

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;
  }

  function toggleDrawer() {
    if (isOpen) closeDrawer();
    else openDrawer();
  }

  // Entry point for the mobile-menu handoff: open with text already typed.
  function openWithQuery(term) {
    openDrawer();
    if (!term) return;
    input.value = term;
    syncClearButton();
    var trimmed = term.trim();
    if (predictiveEnabled && trimmed.length >= minChars) {
      runSearch(trimmed); // skip the debounce; the shopper already committed
    } else {
      showZeroState();
    }
  }

  // ---------- Fetch ----------
  function buildUrl(term) {
    return suggestUrl +
      '?q=' + encodeURIComponent(term) +
      '&resources[type]=' + encodeURIComponent(resourceTypes) +
      '&resources[limit]=' + productLimit +
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last' +
      '&section_id=' + RESULTS_SECTION_ID;
  }

  function cancelInFlight() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (currentFetch) {
      try { currentFetch.abort(); } catch (e) { /* already settled */ }
      currentFetch = null;
    }
  }

  function cachePut(key, html) {
    if (cache.has(key)) cache.delete(key); // refresh recency
    cache.set(key, html);
    if (cache.size > CACHE_CAP) {
      cache.delete(cache.keys().next().value); // Map is insertion-ordered
    }
  }

  function extractSection(text) {
    var doc = new DOMParser().parseFromString(text, 'text/html');
    var node = doc.querySelector('#shopify-section-' + RESULTS_SECTION_ID);
    return node ? node.innerHTML : '';
  }

  function runSearch(term) {
    cancelInFlight();

    if (cache.has(term)) {
      injectResults(cache.get(term), term); // synchronous — no loading flash
      return;
    }

    // abort() cancels the network, but a response can already be resolving inside
    // .then() when it lands. The seq token discards those late resolvers.
    var seq = ++requestSeq;
    var controller = new AbortController();
    currentFetch = controller;

    showLoading();

    fetch(buildUrl(term), { signal: controller.signal, credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Search request failed: ' + res.status);
        return res.text();
      })
      .then(function (text) {
        if (seq !== requestSeq) return;
        var html = extractSection(text);
        cachePut(term, html);
        injectResults(html, term);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (seq !== requestSeq) return;
        showError();
      })
      .then(function () {
        if (currentFetch === controller) currentFetch = null;
      });
  }

  // ---------- Render ----------
  function setState(state) {
    currentState = state;
    if (state === 'zero') {
      if (zoneZero) zoneZero.hidden = false;
      if (zoneResults) zoneResults.hidden = true;
    } else {
      if (zoneZero) zoneZero.hidden = true;
      if (zoneResults) zoneResults.hidden = false;
    }
  }

  function skeletonMarkup() {
    var card =
      '<div class="oddit-search-drawer__skeleton-card">' +
        '<div class="oddit-search-drawer__skeleton-cell oddit-search-drawer__skeleton-cell--media"></div>' +
        '<div class="oddit-search-drawer__skeleton-cell oddit-search-drawer__skeleton-cell--line"></div>' +
        '<div class="oddit-search-drawer__skeleton-cell oddit-search-drawer__skeleton-cell--line-short"></div>' +
      '</div>';
    var cards = '';
    for (var i = 0; i < productLimit; i++) cards += card;
    return '<div class="oddit-search-drawer__skeleton-grid" aria-hidden="true">' + cards + '</div>';
  }

  function showZeroState() {
    setState('zero');
    lastQuery = '';
    if (zoneResults) {
      zoneResults.classList.remove('is-stale');
      zoneResults.innerHTML = '';
    }
    if (predictiveEnabled) input.setAttribute('aria-expanded', 'false');
    rebuildOptions();
  }

  // First query of the session gets a skeleton. After that, keep the previous
  // results mounted and dimmed — blanking the panel on every keystroke is what
  // makes predictive search feel janky.
  function showLoading() {
    setState('live');
    if (!hasRenderedResults) {
      zoneResults.innerHTML = skeletonMarkup();
      rebuildOptions(); // drops aria-activedescendant, which now points at nothing
    } else {
      // Keep the old options arrowable while the replacement is in flight.
      zoneResults.classList.add('is-stale');
    }
    announce('Searching…');
  }

  function injectResults(html, term) {
    hasRenderedResults = true;
    lastQuery = term;

    zoneResults.classList.remove('is-stale');
    zoneResults.innerHTML = html;
    setState('live');

    rebuildOptions();

    // The results section stamps this rather than us sniffing innerHTML, so the
    // check stays locale-independent.
    var wrapper = zoneResults.querySelector('[data-search-has-results]');
    var hasResults = wrapper ? wrapper.getAttribute('data-search-has-results') === 'true' : options.length > 0;

    if (predictiveEnabled) input.setAttribute('aria-expanded', hasResults ? 'true' : 'false');

    if (!hasResults) {
      announce('No results for ' + term);
      renderZeroStateFallback();
    } else {
      announce(options.length + ' results available.');
    }
  }

  // A dead end is a bad search experience: when nothing matches, still offer the
  // merchant's popular searches beneath the empty message.
  function renderZeroStateFallback() {
    if (!zoneZero) return;
    var pills = zoneZero.querySelector('[data-search-popular]');
    if (!pills) return;
    var clone = pills.cloneNode(true);
    zoneResults.appendChild(clone);
    rebuildOptions();
  }

  function showError() {
    setState('live');
    zoneResults.classList.remove('is-stale');
    zoneResults.innerHTML =
      '<p class="oddit-search-drawer__error">We couldn’t load results just now.</p>' +
      '<div class="oddit-search-drawer__error-actions">' +
        '<button type="button" class="oddit-search-drawer__retry" data-search-retry>Try again</button>' +
      '</div>';
    if (predictiveEnabled) input.setAttribute('aria-expanded', 'false');
    announce('Search is unavailable right now.');
    rebuildOptions();
  }

  // ---------- Roving highlight ----------
  // The results HTML is replaced on every keystroke, so option IDs have to be
  // stamped after each injection rather than authored in Liquid.
  function rebuildOptions() {
    // Strip IDs across the whole drawer before restamping. The hidden pane still
    // holds options from the last render, and leaving their IDs in place would
    // duplicate the ones we are about to assign — aria-activedescendant then
    // resolves to whichever the browser finds first, which may be invisible.
    var stale = drawer.querySelectorAll('[data-search-result]');
    for (var s = 0; s < stale.length; s++) {
      stale[s].removeAttribute('id');
      stale[s].setAttribute('aria-selected', 'false');
      stale[s].classList.remove('is-active');
    }

    var container = currentState === 'zero' ? zoneZero : zoneResults;
    options = container ? Array.prototype.slice.call(container.querySelectorAll('[data-search-result]')) : [];
    for (var i = 0; i < options.length; i++) {
      options[i].id = 'oddit-search-opt-' + i;
    }
    activeIndex = -1;
    input.removeAttribute('aria-activedescendant');
  }

  function setActive(index) {
    if (!options.length) return;
    if (activeIndex > -1 && options[activeIndex]) {
      options[activeIndex].classList.remove('is-active');
      options[activeIndex].setAttribute('aria-selected', 'false');
    }
    activeIndex = index;
    var el = options[index];
    if (!el) return;
    el.classList.add('is-active');
    el.setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', el.id);
    el.scrollIntoView({ block: 'nearest' });
  }

  function clearActive() {
    if (activeIndex > -1 && options[activeIndex]) {
      options[activeIndex].classList.remove('is-active');
      options[activeIndex].setAttribute('aria-selected', 'false');
    }
    activeIndex = -1;
    input.removeAttribute('aria-activedescendant');
  }

  function moveActive(delta) {
    if (!options.length) return;
    var next;
    if (activeIndex === -1) next = delta > 0 ? 0 : options.length - 1;
    else next = (activeIndex + delta + options.length) % options.length;
    setActive(next);
  }

  // ---------- Input ----------
  function syncInputWidth() {
    if (!input.value) {
      input.classList.remove('oddit-search-drawer__input--sized');
      input.style.width = '';
      return;
    }

    if (!inputSizer) {
      inputSizer = document.createElement('span');
      inputSizer.className = 'oddit-search-drawer__input-sizer';
      inputSizer.setAttribute('aria-hidden', 'true');
      (form || input.parentNode).appendChild(inputSizer);
    }

    var styles = window.getComputedStyle(input);
    inputSizer.style.fontFamily = styles.fontFamily;
    inputSizer.style.fontSize = styles.fontSize;
    inputSizer.style.fontWeight = styles.fontWeight;
    inputSizer.style.fontStyle = styles.fontStyle;
    inputSizer.style.letterSpacing = styles.letterSpacing;
    inputSizer.style.textTransform = styles.textTransform;
    inputSizer.textContent = input.value;

    var width = inputSizer.scrollWidth;
    if (!width) {
      input.classList.remove('oddit-search-drawer__input--sized');
      input.style.width = '';
      return;
    }

    input.classList.add('oddit-search-drawer__input--sized');
    input.style.width = Math.ceil(width) + 4 + 'px';
  }

  function syncClearButton() {
    syncInputWidth();
    if (!clearBtn) return;
    clearBtn.classList.toggle('is-hidden', !input.value);
  }

  function onInput() {
    syncClearButton();
    if (!predictiveEnabled) return;

    var term = input.value.trim();
    if (term.length < minChars) {
      cancelInFlight();
      showZeroState();
      return;
    }
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      runSearch(term);
    }, DEBOUNCE_MS);
  }

  function onInputKeydown(e) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveActive(-1);
        break;
      case 'Home':
        if (options.length) {
          e.preventDefault();
          setActive(0);
        }
        break;
      case 'End':
        if (options.length) {
          e.preventDefault();
          setActive(options.length - 1);
        }
        break;
      case 'Enter':
        // No highlight: fall through to the form's native GET submit.
        if (activeIndex > -1 && options[activeIndex] && options[activeIndex].href) {
          e.preventDefault();
          window.location.href = options[activeIndex].href;
        }
        break;
      case 'Escape':
        // Clear first, close second. Losing the whole overlay mid-query is a
        // worse mistake to make than an extra keypress.
        e.preventDefault();
        if (input.value) {
          input.value = '';
          syncClearButton();
          cancelInFlight();
          showZeroState();
          announce('');
        } else {
          closeDrawer();
        }
        break;
      default:
        break;
    }
  }

  // ---------- Wiring ----------
  function bindListeners() {
    drawer.querySelectorAll('[data-search-close]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeDrawer();
      }, { signal: signal });
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        input.value = '';
        syncClearButton();
        cancelInFlight();
        showZeroState();
        input.focus();
      }, { signal: signal });
    }

    input.addEventListener('input', onInput, { signal: signal });
    input.addEventListener('keydown', onInputKeydown, { signal: signal });

    if (form) {
      form.addEventListener('click', function (e) {
        if (e.target === form) input.focus();
      }, { signal: signal });

      form.addEventListener('submit', function () {
        // Let the native GET to routes.search_url proceed; just release the lock
        // so the next page doesn't inherit overflow:hidden from a bfcache restore.
        unlockBodyScroll();
      }, { signal: signal });
    }

    // Keep hover and keyboard highlight in sync so they never diverge.
    drawer.addEventListener('pointerover', function (e) {
      var opt = e.target instanceof Element ? e.target.closest('[data-search-result]') : null;
      if (!opt) return;
      var idx = options.indexOf(opt);
      if (idx > -1 && idx !== activeIndex) setActive(idx);
    }, { signal: signal });

    drawer.addEventListener('click', function (e) {
      var retry = e.target instanceof Element ? e.target.closest('[data-search-retry]') : null;
      if (retry) {
        e.preventDefault();
        var term = input.value.trim();
        if (term.length >= minChars) {
          cache.delete(term);
          runSearch(term);
        } else {
          showZeroState();
        }
      }
    }, { signal: signal });

    // Escape when focus has left the input but is still inside the panel;
    // otherwise delegate to the focus trap. Same shape as the cart drawer.
    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape' && document.activeElement !== input) {
        closeDrawer();
      } else {
        trapFocus(e);
      }
    }, { signal: signal });

    window.addEventListener('resize', onViewportChange, { signal: signal });
    window.addEventListener('orientationchange', onViewportChange, { signal: signal });

    // bfcache restore and back/forward should never surface a stuck overlay.
    window.addEventListener('pagehide', function () {
      if (isOpen) closeDrawer();
    }, { signal: signal });
    window.addEventListener('popstate', function () {
      if (isOpen) closeDrawer();
    }, { signal: signal });

    document.addEventListener('search:open', function () { openDrawer(); }, { signal: signal });
    document.addEventListener('search:close', function () { closeDrawer(); }, { signal: signal });

    document.addEventListener('shopify:section:unload', handleSectionUnload, { signal: signal });
  }

  // ---------- Lifecycle ----------
  function handleSectionUnload(e) {
    if (!e.target || !e.target.contains(drawer)) return;
    destroy();
  }

  function destroy() {
    cancelInFlight();
    try { abortController.abort(); } catch (e) { /* noop */ }
    unlockBodyScroll();
    if (window.OdditSearchDrawer && window.OdditSearchDrawer.__root === drawer) {
      delete window.OdditSearchDrawer;
    }
  }

  function init() {
    // Never promise a listbox that can't populate.
    if (predictiveEnabled) {
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
    }
    bindListeners();
    setState('zero');
    rebuildOptions();
  }

  init();

  // ---------- Public API ----------
  // assets/oddit-section-header.js has been calling .open() on this since before
  // the module existed; openWithQuery() serves the mobile-menu handoff.
  window.OdditSearchDrawer = {
    __root: drawer,
    open: openDrawer,
    close: closeDrawer,
    toggle: toggleDrawer,
    openWithQuery: openWithQuery
  };
})();
